import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '../../lib/api-client.js';
import type { FinancePeriod } from './use-finance-summary.js';

export interface WaterfallItem {
  label: string;
  amount: number;
  type: 'revenue' | 'expense' | 'net';
  running_total: number;
}

export interface CumulativeWeek {
  week_ending: string;
  revenue: number;
  payroll: number;
  expenses: number;
  net: number;
}

export interface CashPositionItem {
  label: string;
  amount: number;
  type: 'income' | 'expense' | 'net';
}

export interface CashflowData {
  period: FinancePeriod;
  waterfall: WaterfallItem[];
  cumulative: CumulativeWeek[];
  cash_position: CashPositionItem[];
}

interface CashflowResponse extends CashflowData { error?: string; }

export function useFinanceCashflow(periodType: string) {
  const [data, setData] = useState<CashflowData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      if (periodType) params.set('period', periodType);
      const resp = await apiFetch<CashflowResponse>(`/api/finance/cashflow?${params.toString()}`);
      if (resp?.error) throw new Error(resp.error);
      setData(resp);
    } catch (err) { setData(null); setError(err instanceof Error ? err.message : 'Cashflow load failed'); }
    finally { setLoading(false); }
  }, [periodType]);

  useEffect(() => { load(); }, [load]);
  return { data, loading, error, refresh: load };
}
